import { defaultAmountFormatter, getFormatter } from "./util.js";
import { getAmountFormatting } from "./locales.js";

/**
 * @typedef {(n: number) => string} Formatter
 **/
/**
 * @typedef {"AED" | "AFN" | "ALL" | "AMD" | "ANG" | "AOA" | "ARS" | "AUD" | "AWG" | "AZN" | "BAM" | "BBD" | "BDT" | "BGN" | "BHD" | "BIF" | "BMD" | "BND" | "BOB" | "BRL" | "BSD" | "BTN" | "BWP" | "BYR" | "BZD" | "CAD" | "CDF" | "CHF" | "CLP" | "CNY" | "COP" | "CRC" | "CUP" | "CVE" | "CZK" | "DJF" | "DKK" | "DOP" | "DZD" | "EEK" | "EGP" | "ERN" | "ETB" | "EUR" | "FJD" | "FKP" | "GBP" | "GEL" | "GHS" | "GIP" | "GMD" | "GNF" | "GTQ" | "GYD" | "HKD" | "HNL" | "HRK" | "HTG" | "HUF" | "IDR" | "ILS" | "INR" | "IQD" | "IRR" | "ISK" | "JMD" | "JOD" | "JPY" | "KES" | "KGS" | "KHR" | "KMF" | "KPW" | "KRW" | "KWD" | "KYD" | "KZT" | "LAK" | "LBP" | "LKR" | "LRD" | "LSL" | "LTL" | "LVL" | "LYD" | "MAD" | "MDL" | "MGA" | "MKD" | "MMK" | "MNT" | "MOP" | "MRO" | "MUR" | "MVR" | "MWK" | "MXN" | "MYR" | "MZN" | "NAD" | "NGN" | "NIO" | "NOK" | "NPR" | "NZD" | "OMR" | "PAB" | "PEN" | "PGK" | "PHP" | "PKR" | "PLN" | "PYG" | "QAR" | "RON" | "RSD" | "RUB" | "RWF" | "SAR" | "SBD" | "SCR" | "SDG" | "SEK" | "SGD" | "SHP" | "SLL" | "SOS" | "SRD" | "STD" | "SVC" | "SYP" | "SZL" | "THB" | "TJS" | "TND" | "TOP" | "TRY" | "TTD" | "TWD" | "TZS" | "UAH" | "UGX" | "USD" | "UYU" | "UZS" | "VED" | "VEF" | "VES" | "VND" | "VUV" | "WST" | "XAF" | "XCD" | "XOF" | "XPF" | "YER" | "ZAR" | "ZMK" | "ZWD" } CurrencyCode
 */
/**
 * @typedef {(n: number, currnecy: CurrencyCode) => string} CurrencyFormatter
 */

/**
 * @typedef {Intl.NumberFormatOptions} NumberFormatOptions
 */
/**
 * Returns a function which formats numbers based on the locale.
 *
 * Should be used when performing multiple formatting operations.
 * For example by constructing the formatter outside a loop and using the returned function inside the loop.
 *
 * @param {import("@borealis/locale-common").NMPLocale} locale - one of the supported locale codes
 * @param {NumberFormatOptions} options - options for controlling how formatting is done (number of decimal places, etc.)
 * @returns {Formatter}
 */
export function getNumberFormatter(locale, options = {}) {
	const formatter = getFormatter(locale, options);
	return formatter.format;
}

/**
 * Returns a function which formats a number as currency based on the locale.
 *
 * Should be used when performing multiple formatting operations.
 * For example by constructing the formatter outside a loop and using the returned function inside the loop.
 *
 * @typedef CurrencyFormatingOptions
 * @property {boolean} [removeTrailingPunctuation=false] - enables removing the trailing punctuation from the string
 *
 * @typedef {NumberFormatOptions & CurrencyFormatingOptions} CurrencyFormatOptions
 *
 * @param {import("@borealis/locale-common").NMPLocale} locale - one of the supported locale codes
 * @param {CurrencyFormatOptions} [options] - options for controlling how formatting is done (number of decimal places, etc.)
 * @returns {CurrencyFormatter}
 */
export function getNumberAsCurrencyFormatter(locale, options = {}) {
	const { amountFormatters, currencyCode } = getAmountFormatting(locale);
	const formatter = getNumberFormatter(locale, options);
	return (number, currency = currencyCode) => {
		const amountFormatter =
			amountFormatters[currency] || defaultAmountFormatter(currency);
		const formatted = amountFormatter(formatter(number));
		if (options.removeTrailingPunctuation) {
			// Remove trailing punctuation
			return formatted.substring(0, formatted.length - 1);
		}
		return formatted;
	};
}

/**
 * Returns a function which formats numbers as a percentage based on the locale.
 *
 * Should be used when performing multiple formatting operations.
 * For example by constructing the formatter outside a loop and using the returned function inside the loop.
 *
 * @param {import("@borealis/locale-common").NMPLocale} locale - one of the supported locale codes
 * @param {NumberFormatOptions} options - options for controlling how formatting is done (number of decimal places, etc.)
 * @returns {Formatter}
 */
export const getPercentageFormatter = (locale, options = {}) => {
	return getNumberFormatter(locale, { style: "percent", ...options });
};

/**
 * Function returning a number formated as a string based on the locale.
 *
 * Should only be used for one time translations, see #getNumberFormatter for options
 *
 * @param {number} number - the number to format
 * @param {import("@borealis/locale-common").NMPLocale} locale - locale to use
 * @param {NumberFormatOptions} [options]
 * @return {string} formated number
 */
export const formatNumber = (number, locale, options) => {
	return getNumberFormatter(locale, options)(number);
};

/**
 * Function returning a number formated with the currency for the locale.
 *
 * Should only be used for one time translations, see #getNumberAsCurrencyFormatter for options
 *
 * @param {number} number - number to format
 * @param {import("@borealis/locale-common").NMPLocale} locale - current locale
 * @param {CurrencyCode} currency
 * @param {NumberFormatOptions} [options]
 */
export const formatCurrency = (number, locale, currency, options) => {
	// @ts-ignore
	return getNumberAsCurrencyFormatter(locale, options)(number, currency);
};

/**
 * Function returning a number formated as a percentage for the locale.
 *
 * Should only be used for one time translations, see #getPercentageFormatter for options
 *
 * @param {number} number - number to format
 * @param {import("@borealis/locale-common").NMPLocale} locale - current locale
 * @param {NumberFormatOptions} [options]
 */
export const formatPercentage = (number, locale, options) => {
	return getPercentageFormatter(locale, options)(number);
};
